/** Shared spacing resolution for layout components (Flex, Stack, Grid): resolves SDL spacing tokens to CSS values from the const spacing scale. */

import { spacingScale as spacing } from '@federa/const/design-token-scales';
import { asStringOrUndefined } from '@federa/const/string-guards';

/** Everything a gap accepts: an SDL spacing token key, a raw CSS string, a pixel number, or nothing. */
type GapValue = keyof typeof spacing | string | number | undefined;

/** Resolve a gap value to a CSS string: SDL spacing token keys map to their token value, numbers become `<n>px`, other strings pass through, undefined returns undefined. */
export function resolveGap(value: GapValue): string | undefined {
	if (value === undefined) return undefined;
	if (typeof value === 'number') return `${value}px`;
	if (typeof value === 'string' && value in spacing) {
		const spacingValue = spacing[value as keyof typeof spacing];
		return asStringOrUndefined(spacingValue);
	}
	return value;
}

/** Same as resolveGap but returns `defaultValue` instead of undefined. */
export function resolveGapWithDefault(value: GapValue, defaultValue: string): string {
	return resolveGap(value) ?? defaultValue;
}
